<template>
    <div class="import-feedback-drawer-settings-step">
        <div class="import-feedback-drawer-settings-step__title">
            Settings
        </div>
        <div class="import-feedback-drawer-settings-step__content">
            <div class="import-feedback-drawer-settings-step__guide-card">
                <p class="import-feedback-drawer-settings-step__label">
                    Selected {{ type }}:
                </p>
                <pendo-divider
                    direction="vertical"
                    decorative
                    width="32px" />
                <div class="import-feedback-drawer-settings-step__guide-name-wrapper">
                    <pendo-router-link
                        :to="{ name: 'GuideDetails', params: { guideId: guide.id } }"
                        target="_blank">
                        <pendo-button
                            class="import-feedback-drawer-settings-step__guide-name"
                            :label="guide.name"
                            theme="app"
                            height="20px"
                            type="subtle" />
                    </pendo-router-link>
                    <pendo-icon
                        type="external-link"
                        size="16"
                        stroke="var(--palette-teal-80)"
                        class="import-feedback-drawer-settings-step__external-icon" />
                </div>
                <pendo-divider
                    direction="vertical"
                    decorative
                    width="32px" />
                <pendo-app-cell :row="guide.appItem" />
                <pendo-divider
                    direction="vertical"
                    decorative
                    width="32px" />
                <pendo-group-cell :row="guide" />
            </div>
            <div class="import-feedback-drawer-settings-step__toggles">
                <p class="import-feedback-drawer-settings-step__toggle-label">
                    Automatically send future responses to Listen
                </p>
                <pendo-toggle
                    v-model="localAutoImport"
                    @input="emitAutomaticImportChange" />
                <p class="import-feedback-drawer-settings-step__toggle-label">
                    Send historic responses to Listen
                </p>
                <div>
                    <pendo-toggle
                        v-model="localHistoricImport"
                        data-cy="import-feedback-drawer-settings-step-historic-toggle"
                        @input="emitHistoricImportChange" />
                </div>
            </div>
            <div
                v-if="localHistoricImport"
                class="import-feedback-drawer-settings-step__historic-import-settings">
                <div class="import-feedback-drawer-settings-step__range">
                    <p class="import-feedback-drawer-settings-step__date-label">
                        Date range
                    </p>
                    <time-series-picker
                        :time-series="timeSeries"
                        :shortcuts="shortcuts"
                        @change="updateTimeframe" />
                </div>
                <div class="import-feedback-drawer-settings-step__responses">
                    <p class="import-feedback-drawer-settings-step__label">
                        Responses:
                    </p>
                    <span
                        v-pendo-loading="loadingResponseCount"
                        class="import-feedback-drawer-settings-step__response-count">
                        {{ responseCount }}
                    </span>
                </div>
            </div>
            <pendo-divider height="48px" />
            <div class="import-feedback-drawer-settings-step__ai-settings">
                <p class="import-feedback-drawer-settings-step__ai-title">
                    AI settings
                </p>
                <div class="import-feedback-drawer-settings-step__ai-subtitle">
                    Which responses should be analyzed by AI?
                    <learn-more-link
                        href="https://support.pendo.io/hc/en-us/articles/27879734620059-Artificial-intelligence-AI-at-Pendo" />
                </div>
                <table>
                    <tbody>
                        <tr>
                            <th>Question</th>
                            <th>Do not analyze</th>
                            <th>Analyze</th>
                            <th>
                                <pendo-checkbox
                                    v-model="analyzeAllToggle"
                                    data-cy="import-feedback-drawer-settings-step-analyze-all-checkbox"
                                    class="import-feedback-drawer-settings-step__checkbox"
                                    @change="updateAllToggles" />
                                Analyze all
                            </th>
                        </tr>
                        <tr
                            v-for="question in guide.polls"
                            :key="question.id">
                            <pendo-radio-group
                                :value="question.analyze"
                                class="import-feedback-drawer-settings-step__radio-group"
                                @change="updateAnalyzeValue(question.id, $event)">
                                <td>{{ sanitizePollQuestion(question.question) }}*</td>
                                <td><pendo-radio value="do-not-analyze" /></td>
                                <td><pendo-radio value="analyze" /></td>
                            </pendo-radio-group>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="import-feedback-drawer-settings-step__information-wrapper">
                <div
                    v-for="(infoMsg, index) in infoMsgs"
                    :key="index"
                    class="import-feedback-drawer-settings-step__information">
                    <pendo-icon
                        type="info"
                        size="16"
                        stroke="var(--palette-gray-70)" />
                    <p class="import-feedback-drawer-settings-step__information-text">
                        {{ infoMsg }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    PendoButton,
    PendoDivider,
    PendoAppCell,
    PendoGroupCell,
    PendoToggle,
    PendoLoading,
    PendoIcon,
    PendoTooltip,
    PendoCheckbox,
    PendoRadioGroup,
    PendoRadio,
} from '@pendo/components';
import TimeSeriesPicker from '@/common/time-series-picker/time-series-picker.vue';
import { convertConfigToTimeSeries } from '@/common/time-series-picker/time-series-picker.util';
import ImportGuidePollResponseSummaryAggregation from '@/voc/aggregations/import-guide-poll-response-summary/import-guide-poll-response-summary.aggregation.js';
import ImportNpsPollResponseSummaryAggregation from '@/voc/aggregations/import-nps-poll-response-summary/import-nps-poll-response-summary.aggregation.js';
import { runAggregationOnce } from '@/utils/aggregation';
import moment from '@/utils/moment-wrapper';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { sanitizePollQuestion } from '@/utils/guide.utils';
import { roundResponseCount } from '@/utils/voc-polls.utils';
import LearnMoreLink from '@/common/learn-more-link/learn-more-link.vue';

export const PICKER_SHORTCUTS = [
    {
        label: 'Last 30 Days (including today)',
        value: {
            start: 0,
            end: -29,
        },
    },
    {
        label: 'Last 90 Days (including today)',
        value: {
            start: 0,
            end: -89,
        },
    },
    {
        label: 'Last 180 Days (including today)',
        value: {
            start: 0,
            end: -179,
        },
    },
    {
        label: 'All Time',
        value: {
            start: 0,
            end: null,
        },
    },
];

export default {
    name: 'ImportFeedbackDrawerSettingsStep',
    components: {
        PendoButton,
        PendoDivider,
        PendoAppCell,
        PendoGroupCell,
        PendoToggle,
        TimeSeriesPicker,
        PendoIcon,
        PendoRouterLink,
        PendoCheckbox,
        PendoRadioGroup,
        PendoRadio,
        LearnMoreLink,
    },
    directives: {
        PendoLoading,
        PendoTooltip,
    },
    props: {
        guide: {
            type: Object,
            required: true,
        },
        poll: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
    },
    emits: [
        'automatic-import-change',
        'historic-import-change',
        'update-import-count',
        'update-timeframe',
        'update-ai-flag',
    ],
    data () {
        return {
            localAutoImport: true,
            localHistoricImport: false,
            timeConfig: {
                type: 'Last 90 Days (including today)',
                start: moment()
                    .clone()
                    .subtract(89, 'days')
                    .format('YYYY-MM-DD'),
                end: moment()
                    .clone()
                    .add(1, 'days')
                    .format('YYYY-MM-DD'),
            },
            loadingResponseCount: false,
            responseCount: 0,
            analyzeAllToggle: false,
        };
    },
    computed: {
        timeSeries () {
            return convertConfigToTimeSeries(this.timeConfig);
        },
        shortcuts () {
            return PICKER_SHORTCUTS;
        },
        infoMsgs () {
            let infoMsgs = [];
            switch (this.type) {
                case 'guide':
                    infoMsgs = [
                        'Poll responses will be shown as one item in Listen and we will import all submissions, where 1 or more questions have been answered',
                    ];
                    break;
                case 'NPS':
                    infoMsgs = ['Only responses with text will be imported'];
                    break;
            }

            return infoMsgs;
        },
    },
    created () {
        this.updateTimeframe({ config: this.timeConfig });
        this.$emit('historic-import-change', false);
    },
    methods: {
        sanitizePollQuestion,
        updateTimeframe ({ config }) {
            this.timeConfig = config;
            this.$emit('update-timeframe', this.timeConfig);
            this.updateResponseCount();
        },
        async updateResponseCount () {
            try {
                this.$emit('update-import-count', null);
                this.loadingResponseCount = true;
                this.responseCount = 0;
                let endTime = 'now()';
                let startTime = this.guide.createdAt;
                const timeFrameOption = this.timeConfig.type;
                if (timeFrameOption === 'Custom') {
                    startTime = moment(this.timeConfig.start, 'YYYY-MM-DD')
                        .add(1, 'days')
                        .valueOf()
                        .toString();
                    endTime = moment(this.timeConfig.end, 'YYYY-MM-DD')
                        .valueOf()
                        .toString();
                } else if (timeFrameOption.includes('Last')) {
                    startTime = moment(this.timeConfig.start, 'YYYY-MM-DD')
                        .valueOf()
                        .toString();
                }
                let guideResponseAggData;
                if (this.type === 'NPS') {
                    guideResponseAggData = await runAggregationOnce(ImportNpsPollResponseSummaryAggregation, {
                        npsReasonId: this.poll.attributes.followUp,
                        guideId: this.guide.id,
                        startTime,
                        endTime,
                    });
                } else {
                    guideResponseAggData = await runAggregationOnce(ImportGuidePollResponseSummaryAggregation, {
                        onlyCount: true,
                        guideId: this.guide.id,
                        pollIdToCount: this.guide?.polls?.length ? this.guide.polls[0].id : '',
                        startTime,
                        endTime,
                    });
                }
                this.responseCount = roundResponseCount(guideResponseAggData[0].count);
                this.$emit('update-import-count', this.responseCount);
                this.loadingResponseCount = false;
            } catch {
                this.loadingResponseCount = false;
                this.responseCount = '--';
            }
        },
        emitHistoricImportChange (val) {
            this.$emit('historic-import-change', val);
        },
        emitAutomaticImportChange (val) {
            this.$emit('automatic-import-change', val);
        },
        updateAnalyzeValue (pollId, value) {
            this.$emit('update-ai-flag', pollId, value);
        },
        updateAllToggles (val) {
            const analyzeValue = val ? 'analyze' : 'do-not-analyze';
            this.guide?.polls.forEach((poll) => {
                this.updateAnalyzeValue(poll.id, analyzeValue);
            });
        },
    },
};
</script>

<style lang="less" scoped>
.import-feedback-drawer-settings-step {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background-color: var(--palette-gray-0);
    width: 95%;
    margin: 0 auto;
    border: 1px solid var(--palette-gray-40);
    padding: 24px 32px;
    border-radius: 3px;
    &__content {
        width: 100%;
    }
    &__title {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 16px;
    }
    &__guide-card {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        flex-wrap: wrap;
        align-items: center;
        border: 1px solid var(--palette-gray-40);
        background: var(--palette-gray-10);
        border-radius: 4px;
        padding: 10px 32px;
        line-height: 120%;
    }
    &__toggles {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 16px;
        margin-top: 24px;
        width: 100%;
        max-width: 650px;
    }
    &__responses {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 10px;
    }
    &__label {
        margin: 0;
        font-weight: 600;
    }
    &__response-count {
        line-height: 150%;
        min-width: 30px;
        min-height: 25px;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    &__date-label {
        margin: 0 0 6px 0;
        font-weight: 600;
    }
    &__guide-name {
        height: 20px;
    }
    &__toggle-label {
        margin: 0;
    }
    &__historic-import-settings {
        margin: 16px 0 0 0;
    }
    &__range {
        margin-bottom: 16px;
    }
    &__information-wrapper {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    &__information {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
    }
    &__information-text {
        margin: 0;
        color: var(--palette-gray-70);
    }
    &__external-icon {
        opacity: 0;
        position: absolute;
        right: -20px;
        top: 1px;
        pointer-events: none;
    }
    &__guide-name-wrapper {
        position: relative;
        &:hover {
            .import-feedback-drawer-settings-step__external-icon {
                opacity: 1;
            }
        }
    }
    &__ai-settings {
        margin-bottom: 16px;
        table {
            width: 100%;
            line-height: 150%;
        }
        tr {
            border-bottom: 1px solid var(--palette-gray-40);

            &:first-of-type {
                border-bottom: 2px solid var(--palette-gray-40);
                background: var(--palette-gray-10);
            }

            th:nth-last-child(-n + 3) {
                width: 150px;
            }
        }
        th {
            padding: 10px 16px;
        }
        td {
            padding: 16px;
        }
    }
    &__radio-group {
        display: contents;
        :deep(.pendo-radio-group) {
            display: contents;
        }
    }
    &__ai-title {
        font-weight: 600;
    }
    &__ai-subtitle {
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        gap: 12px;
    }
    &__checkbox {
        margin-right: 4px;
    }
}
</style>
